import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ShoppingCart, Plus, Minus, Search, Package2, DollarSign, X, Check, Mail, Phone, AlertCircle, Trash2, CreditCard } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import { getStockItems, StockItem } from '../lib/stockItems';
import { ProductPhotoService } from '../services/productPhotoService';
import { DiscountCodeService } from '../services/discountCodeService';
import { toast } from '../components/toasts/SimpleToast';
import SelectCustomerModal from '../components/manage/SelectCustomerModal';
import RecordProductSalePaymentModal from '../components/RecordProductSalePaymentModal';
import SendInvoiceModal from '../components/SendInvoiceModal';
import StyledInvoiceDisplay from '../components/StyledInvoiceDisplay';
import { createProductSale } from '../lib/productSales';
import { sendGmailEmail } from '../lib/email';
import { supabase } from '../lib/supabase';

// Custom portal-based toast component for modal contexts
const PortalToast = ({ 
  children, 
  type, 
  onClose 
}: { 
  children: React.ReactNode; 
  type: 'loading' | 'success' | 'error';
  onClose: () => void;
}) => {
  const getIcon = () => {
    switch (type) {
      case 'loading':
        return <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>;
      case 'success':
        return <div className="rounded-full h-4 w-4 bg-green-500 flex items-center justify-center text-white text-xs">✓</div>;
      case 'error':
        return <div className="rounded-full h-4 w-4 bg-red-500 flex items-center justify-center text-white text-xs">✕</div>;
    }
  };

  const getBorderColor = () => {
    switch (type) {
      case 'loading':
        return 'border-blue-200';
      case 'success':
        return 'border-green-200';
      case 'error':
        return 'border-red-200';
    }
  };

  return createPortal(
    <div 
      className={`fixed top-4 left-1/2 transform -translate-x-1/2 bg-white rounded-lg shadow-lg border ${getBorderColor()} p-4 flex items-center space-x-3 min-w-[300px] max-w-md`}
      style={{ zIndex: 999999 }}
    >
      {getIcon()}
      <div className="flex-1">
        {children}
      </div>
      {type !== 'loading' && (
        <button 
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 ml-2"
        >
          ×
        </button>
      )}
    </div>,
    document.body
  );
};

// Custom toast function with portal rendering for modal contexts
const createPortalToast = (
  setShowPortalToast: (show: boolean) => void,
  setPortalToastMessage: (message: React.ReactNode) => void,
  setPortalToastType: (type: 'loading' | 'success' | 'error') => void
) => ({
  loading: (message: React.ReactNode, options?: any) => {
    setPortalToastMessage(message);
    setPortalToastType('loading');
    setShowPortalToast(true);
    return 'portal-toast-loading';
  },
  success: (message: string, options?: any) => {
    setPortalToastMessage(message);
    setPortalToastType('success');
    setShowPortalToast(true);
    // Auto-hide success toast after 4 seconds
    setTimeout(() => setShowPortalToast(false), 4000);
    return 'portal-toast-success';
  },
  error: (message: string, options?: any) => {
    setPortalToastMessage(message);
    setPortalToastType('error');
    setShowPortalToast(true);
    // Auto-hide error toast after 4 seconds
    setTimeout(() => setShowPortalToast(false), 4000);
    return 'portal-toast-error';
  },
  dismiss: (toastId?: string) => {
    setShowPortalToast(false);
  }
});

interface CartItem {
  product: StockItem;
  quantity: number;
}

function SellProducts() {
  const { selectedBusiness } = useBusiness();
  const [items, setItems] = useState<StockItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [productPhotos, setProductPhotos] = useState<Record<string, string>>({});
  
  // Customer and payment states
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isSendingInvoice, setIsSendingInvoice] = useState(false);
  const [loadingToastId, setLoadingToastId] = useState<string | null>(null);
  const [isSendInvoiceModalOpen, setIsSendInvoiceModalOpen] = useState(false);
  const [showPortalToast, setShowPortalToast] = useState(false);
  const [portalToastMessage, setPortalToastMessage] = useState<React.ReactNode>(null);
  const [portalToastType, setPortalToastType] = useState<'loading' | 'success' | 'error'>('loading');

  // Create portal toast instance
  const portalToast = createPortalToast(setShowPortalToast, setPortalToastMessage, setPortalToastType);

  // Discount-related state
  const [discountMode, setDiscountMode] = useState<'code' | 'custom'>('code');
  const [discountCode, setDiscountCode] = useState('');
  const [customDiscountAmount, setCustomDiscountAmount] = useState('');
  const [customDiscountType, setCustomDiscountType] = useState<'%' | '$'>('%');
  const [appliedDiscount, setAppliedDiscount] = useState<{
    id?: string;
    code?: string;
    type: 'percentage' | 'fixed';
    value: number;
    description?: string;
    discountAmount: number;
    source: 'code' | 'custom';
  } | null>(null);
  const [discountError, setDiscountError] = useState<string | null>(null);
  const [discountValidating, setDiscountValidating] = useState(false);

  // Load stock items
  useEffect(() => {
    if (selectedBusiness?.id) {
      loadItems();
    }
  }, [selectedBusiness?.id]);

  const loadItems = async () => {
    if (!selectedBusiness?.id) return;
    
    setLoading(true);
    try {
      const stockItems = await getStockItems(selectedBusiness.id);
      
      // Only show items with quantity > 0 (ignoring is_sellable for now)
      const sellableItems = stockItems.filter(item => 
        item.quantity > 0 && 
        !item.is_deleted
      );
      setItems(sellableItems);
      
      // Load product photos
      const stockItemIds = sellableItems.map(item => item.id);
      if (stockItemIds.length > 0) {
        try {
          const photoMap = await ProductPhotoService.getPhotosByStockItemIds(stockItemIds);
          
          // Convert to the format expected by the component
          const photoUrls: Record<string, string> = {};
          Object.values(photoMap).forEach(photo => {
            photoUrls[photo.stock_item_id] = photo.photo_url;
          });
          
          setProductPhotos(photoUrls);
        } catch (error) {
          console.warn('Failed to load product photos:', error);
        }
      }
    } catch (error) {
      console.error('Failed to load stock items:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter items based on search
  const filteredItems = items.filter(item =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.sku?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Cart functions
  const addToCart = (product: StockItem) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.product.id === product.id);
      if (existingItem) {
        // Check if we can add more
        if (existingItem.quantity >= product.quantity) {
          return prevCart;
        }
        return prevCart.map(item =>
          item.product.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        return [...prevCart, { product, quantity: 1 }];
      }
    });
  };

  const removeFromCart = (productId: string) => {
    setCart(prevCart => prevCart.filter(item => item.product.id !== productId));
  };

  const updateCartQuantity = (productId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }

    const product = items.find(item => item.id === productId);
    if (product && quantity > product.quantity) {
      return;
    }

    setCart(prevCart =>
      prevCart.map(item =>
        item.product.id === productId
          ? { ...item, quantity }
          : item
      )
    );
  };

  const clearCart = () => {
    setCart([]);
    setAppliedDiscount(null);
    setDiscountCode('');
    setCustomDiscountAmount('');
    setDiscountError(null);
  };

  // Discount functions
  const applyDiscountCode = async () => {
    if (!discountCode.trim()) return;
    
    setDiscountValidating(true);
    setDiscountError(null);
    
    try {
      console.log('🔍 Validating discount code:', discountCode, 'for business:', selectedBusiness?.id, 'subtotal:', cartSubtotal);
      const validation = await DiscountCodeService.validateDiscountCode(selectedBusiness?.id || '', discountCode.toUpperCase(), cartSubtotal);
      console.log('✅ Discount validation result:', validation);
      
      if (validation.isValid && validation.code) {
        const discount = validation.code;
        const discountAmount = validation.discountAmount || 0;
          
        setAppliedDiscount({
          id: discount.id,
          code: discount.code,
          type: discount.type,
          value: discount.value,
          description: discount.description,
          discountAmount: discountAmount,
          source: 'code'
        });
        setDiscountCode('');
      } else {
        console.log('❌ Discount validation failed:', validation.error);
        setDiscountError(validation.error || 'Invalid discount code');
      }
    } catch (error: any) {
      console.error('❌ Discount code validation error:', error);
      setDiscountError(error.message || 'Invalid discount code');
    } finally {
      setDiscountValidating(false);
    }
  };

  const applyCustomDiscount = () => {
    const amount = parseFloat(customDiscountAmount);
    if (isNaN(amount) || amount <= 0) {
      setDiscountError('Please enter a valid discount amount');
      return;
    }

    const discountAmount = customDiscountType === '%' 
      ? (cartSubtotal * amount) / 100
      : amount;

    if (discountAmount > cartSubtotal) {
      setDiscountError('Discount cannot exceed subtotal');
      return;
    }

    setAppliedDiscount({
      type: customDiscountType === '%' ? 'percentage' : 'fixed',
      value: amount,
      description: `Custom ${customDiscountType === '%' ? amount + '%' : '$' + amount} discount`,
      discountAmount,
      source: 'custom'
    });
    
    setCustomDiscountAmount('');
    setDiscountError(null);
  };

  const removeDiscount = () => {
    setAppliedDiscount(null);
    setDiscountError(null);
  };

  // Calculate totals
  const cartSubtotal = cart.reduce((total, item) => total + (item.product.price * item.quantity), 0);
  const discountAmount = appliedDiscount ? appliedDiscount.discountAmount : 0;
  const discountedSubtotal = cartSubtotal - discountAmount;
  const cartTaxAmount = discountedSubtotal * 0.05; // 5% tax rate
  const cartTotal = discountedSubtotal + cartTaxAmount;

  const handleCheckout = () => {
    if (cart.length === 0) {
      return;
    }
    setIsCustomerModalOpen(true);
  };

  const handleSendInvoice = () => {
    if (!selectedCustomer) {
      return;
    }
    setIsSendInvoiceModalOpen(true);
  };

  // Generate invoice PDF from StyledInvoiceDisplay (copied from Stock.tsx)
  const generateProductInvoicePDF = async (productSale: any): Promise<{ blob: Blob; filename: string } | null> => {
    try {
      // Import libraries dynamically
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf')
      ]);
      
      // Create a temporary invoice display element
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '-9999px';
      tempContainer.style.width = '800px';
      tempContainer.style.backgroundColor = '#ffffff';
      tempContainer.style.padding = '20px';
      
      // Create React root for the temporary container
      const { createRoot } = await import('react-dom/client');
      const root = createRoot(tempContainer);
      
      // Transform product sale data to match the expected format
      const invoiceTotals = {
        subtotal: productSale.subtotal_amount,
        tax_amount: productSale.tax_amount,
        total: productSale.total_amount,
        total_discounts: productSale.discount_amount || 0
      };

      // Create sale data for StyledInvoiceDisplay (matching ProductSaleSuccessModal format)
      const saleForInvoice = {
        saleNumber: productSale.sale_number,
        paymentMethod: 'invoice', // Required field
        paymentStatus: 'unpaid' as const, // Invoice is unpaid when sent
        amountPaid: 0, // No payment collected yet
        saleDate: new Date().toISOString(), // Required field
        saleData: {
          subtotal: invoiceTotals.subtotal,
          subtotal_after_discounts: invoiceTotals.subtotal - (invoiceTotals.total_discounts || 0),
          taxAmount: invoiceTotals.tax_amount,
          total: invoiceTotals.total,
          total_discounts: invoiceTotals.total_discounts,
          // Transform discount_details to the format expected by StyledInvoiceDisplay
          discounts: productSale.discount_details ? productSale.discount_details.map((discount: any) => ({
            id: discount.id || 'discount-1',
            title: discount.title,
            type: discount.type,
            amount: discount.amount
          })) : [],
          items: productSale.items.map((item: any) => ({
            name: item.product_name, // Changed from 'id' to match ProductSaleItem interface
            quantity: item.quantity,
            price: item.unit_price
            // Removed extra fields that aren't in ProductSaleItem interface
          }))
        }
      };
      
      document.body.appendChild(tempContainer);
      
      // Render the invoice component
      await new Promise<void>((resolve, reject) => {
        try {
          console.log('🎨 Rendering StyledInvoiceDisplay with data:', {
            saleForInvoice,
            business: selectedBusiness?.name,
            customer: selectedCustomer?.first_name + ' ' + selectedCustomer?.last_name
          });
          
          root.render(
            React.createElement(StyledInvoiceDisplay, {
              sale: saleForInvoice,
              business: selectedBusiness!,
              customer: selectedCustomer,
              className: 'invoice-pdf-generation'
            })
          );
          
          // Wait for rendering to complete
          setTimeout(resolve, 1000);
        } catch (renderError) {
          console.error('❌ Error rendering StyledInvoiceDisplay:', renderError);
          reject(renderError);
        }
      });

      // Convert to canvas
      console.log('📸 Converting to canvas...');
      const canvas = await html2canvas(tempContainer, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });
      console.log('✅ Canvas created:', { width: canvas.width, height: canvas.height });

      // Create PDF
      console.log('📄 Creating PDF...');
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');

      // Calculate dimensions to fit A4
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 295; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      let position = 0;

      // Add first page
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Add additional pages if needed
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Clean up
      root.unmount();
      document.body.removeChild(tempContainer);

      const blob = pdf.output('blob');
      const filename = `invoice-${productSale.sale_number}.pdf`;
      
      return { blob, filename };
    } catch (error) {
      console.error('Error generating product invoice PDF:', error);
      return null;
    }
  };

  const handleSendInvoiceMethod = async (method: 'email' | 'sms', allowStripePay: boolean) => {
    if (method === 'email') {
      await handleSendInvoiceEmail(allowStripePay);
    } else {
      // TODO: Implement SMS sending
      console.log('SMS sending not yet implemented for product invoices');
    }
  };

  const handleSendInvoiceEmail = async (allowStripePay: boolean = true) => {
    console.log('🚀 Starting invoice email process...');
    console.log('Selected customer:', selectedCustomer);
    console.log('Selected business:', selectedBusiness);
    console.log('Cart items:', cart);
    
    if (!selectedCustomer?.email || !selectedBusiness) {
      console.error('Missing required data for invoice sending');
      return;
    }

    if (cart.length === 0) {
      console.error('Cart is empty');
      return;
    }

    try {
      // Set loading state and show portal toast - same as booking payment summary
      setIsSendingInvoice(true);
      
      const loadingToast = portalToast.loading(
        <div>
          <div>Sending invoice...</div>
          <div style={{ fontSize: '12px', opacity: 0.7, marginTop: '2px' }}>don't close store</div>
        </div>
      );

      // Generate a temporary invoice number for the pending invoice
      const invoiceNumber = `INV-${Date.now()}`;
      const TAX_RATE = 5; // 5% tax rate

      console.log('📧 Creating professional invoice email for cart items...');

      // Create the product sale using the proper function (this will create stock transactions)
      console.log('💾 Creating product sale record...');
      
      const productSale = await createProductSale({
        business_id: selectedBusiness.id,
        customer_id: selectedCustomer.id,
        subtotal_amount: cartSubtotal,
        tax_rate: TAX_RATE,
        tax_amount: cartTaxAmount,
        total_amount: cartTotal,
        discount_amount: discountAmount > 0 ? discountAmount : undefined,
        discount_details: appliedDiscount ? [{
          id: appliedDiscount.id || 'custom',
          title: appliedDiscount.source === 'code' ? `Code: ${appliedDiscount.code}` : (appliedDiscount.description || 'Custom Discount'),
          type: appliedDiscount.type,
          amount: appliedDiscount.value
        }] : undefined,
        original_subtotal: discountAmount > 0 ? cartSubtotal + discountAmount : undefined,
        payment_status: 'unpaid', // Will be updated when payment is received
        notes: `Invoice ${invoiceNumber} - Products sale`,
        items: cart.map(item => ({
          stock_item_id: item.product.id,
          product_name: item.product.name,
          quantity: item.quantity,
          unit_price: item.product.price,
          tax_exempt: false, // Default for now
          tax_rate: TAX_RATE,
          tax_amount: (item.product.price * item.quantity * TAX_RATE) / 100,
          total_price: item.product.price * item.quantity
        }))
      });

      console.log('✅ Product sale record created with stock transactions:', productSale.id, 'Sale number:', productSale.sale_number);
      console.log('Product sale data:', productSale);

      // Add items array to productSale for PDF generation (since createProductSale doesn't return items)
      const productSaleWithItems = {
        ...productSale,
        items: cart.map(item => ({
          stock_item_id: item.product.id,
          product_name: item.product.name,
          quantity: item.quantity,
          unit_price: item.product.price,
          tax_exempt: false,
          tax_rate: TAX_RATE,
          tax_amount: (item.product.price * item.quantity * TAX_RATE) / 100,
          total_price: item.product.price * item.quantity
        }))
      };

      // Now generate PDF with the actual sale data
      console.log('📄 Starting PDF generation...');
      const pdfData = await generateProductInvoicePDF(productSaleWithItems);
      if (!pdfData) {
        console.error('❌ Failed to generate PDF');
        throw new Error('PDF generation failed');
      }
      console.log('✅ PDF generated successfully:', pdfData.filename);

      // Convert blob to base64
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve, reject) => {
        reader.onloadend = () => {
          const base64String = reader.result as string;
          // Remove data:application/pdf;base64, prefix
          const base64Content = base64String.split(',')[1];
          resolve(base64Content);
        };
        reader.onerror = reject;
      });
      reader.readAsDataURL(pdfData.blob);
      
      const base64Content = await base64Promise;

      console.log('PDF attachment prepared:', {
        filename: pdfData.filename,
        contentLength: base64Content.length,
        contentPreview: base64Content.substring(0, 50) + '...'
      });

      // Generate payment link if Stripe Connect is enabled and allowStripePay is true
      let paymentLinkHtml = '';

      if (allowStripePay && (selectedBusiness as any).stripe_connect_account_id && (selectedBusiness as any).online_payments_enabled) {
        console.log('🔍 Creating Stripe payment link for product sale...');
        
        try {
          // Create payment record linked to the product sale
          const { data: pendingPayment, error: paymentError } = await supabase
            .from('payments')
            .insert({
              business_id: selectedBusiness.id,
              booking_id: null, // NULL for product sales
              product_sale_id: productSaleWithItems.id, // Direct foreign key link
              amount: cartTotal,
              method: 'credit_card',
              status: 'pending',
              reference: `Product Sale ${productSaleWithItems.sale_number}`,
              notes: `Payment for product sale ${productSaleWithItems.sale_number} - Product invoice`,
              payment_token: crypto.randomUUID()
            })
            .select()
            .single();

          if (paymentError) {
            console.error('Failed to create payment record:', paymentError);
            console.log('⚠️ Continuing without payment link due to database constraint');
          } else {
            console.log('✅ Pending payment record created:', pendingPayment.id);

            // Generate payment link
            const { data: linkData, error: linkError } = await supabase.functions.invoke('generate-invoice-payment-link', {
              body: {
                paymentId: pendingPayment.id
              }
            });

            if (!linkError && linkData?.paymentUrl) {
              paymentLinkHtml = `
                <div style="margin: 30px 0; padding: 25px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border-radius: 12px; text-align: center;">
                  <h3 style="color: white; margin: 0 0 15px 0; font-size: 18px;">Pay Your Invoice Online</h3>
                  <p style="color: #e2e8f0; margin: 0 0 20px 0; font-size: 14px;">Secure payment with credit or debit card</p>
                  <a href="${linkData.paymentUrl}" 
                     style="display: inline-block; background: #ffffff; color: #667eea; padding: 12px 30px; text-decoration: none; border-radius: 8px; font-weight: bold; font-size: 16px; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
                    Pay $${cartTotal.toFixed(2)} Now
                  </a>
                  <p style="color: #cbd5e0; margin: 15px 0 0 0; font-size: 12px;">Powered by Stripe - Secure &amp; Encrypted</p>
                </div>
              `;
              console.log('✅ Payment link generated:', linkData.paymentUrl);
            } else {
              console.error('Failed to generate payment link:', linkError);
            }
          }
        } catch (paymentLinkError) {
          console.error('Error in payment link generation:', paymentLinkError);
          console.log('⚠️ Continuing without payment link');
        }
      }

      // Prepare email content using the same professional format as booking invoices
      const customerName = `${selectedCustomer.first_name} ${selectedCustomer.last_name}`;
      const actualInvoiceNumber = productSaleWithItems.sale_number || invoiceNumber;
      const emailSubject = `Invoice #${actualInvoiceNumber} from ${selectedBusiness.name}`;

      const emailBody = `
        <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 600px; margin: 0 auto; background: #ffffff;">
          <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 30px; text-align: center; border-radius: 12px 12px 0 0;">
            <h1 style="color: white; margin: 0; font-size: 28px;">Invoice #${actualInvoiceNumber}</h1>
            <p style="color: #e2e8f0; margin: 10px 0 0 0; font-size: 16px;">from ${selectedBusiness.name}</p>
          </div>
          
          <div style="padding: 30px;">
            <p style="color: #374151; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">Hi ${customerName},</p>
            <p style="color: #374151; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">Please find your invoice for the following products:</p>
            
            ${paymentLinkHtml}
            
            <div style="background: #f8fafc; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #667eea;">
              <h4 style="color: #374151; margin: 0 0 10px 0; font-size: 16px;">Invoice Summary</h4>
              <div style="display: flex; justify-content: space-between; margin: 8px 0;">
                <span style="color: #6b7280;">Subtotal:</span>
                <span style="color: #374151; font-weight: bold;">$${cartSubtotal.toFixed(2)}</span>
              </div>
              ${discountAmount > 0 ? `
                <div style="display: flex; justify-content: space-between; margin: 8px 0;">
                  <span style="color: #059669;">Discount:</span>
                  <span style="color: #059669; font-weight: bold;">-$${discountAmount.toFixed(2)}</span>
                </div>
              ` : ''}
              <div style="display: flex; justify-content: space-between; margin: 8px 0;">
                <span style="color: #6b7280;">GST (${TAX_RATE}%):</span>
                <span style="color: #374151; font-weight: bold;">$${cartTaxAmount.toFixed(2)}</span>
              </div>
              <div style="display: flex; justify-content: space-between; margin: 8px 0; padding-top: 8px; border-top: 1px solid #e5e7eb;">
                <span style="color: #374151; font-weight: bold;">Total Amount:</span>
                <span style="color: #059669; font-weight: bold; font-size: 18px;">$${cartTotal.toFixed(2)}</span>
              </div>
            </div>
            
            <p style="color: #374151; font-size: 16px; line-height: 1.6; margin: 20px 0;">Thank you for your business!</p>
            
            <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #e5e7eb;">
              <p style="color: #374151; font-size: 16px; margin: 0;">Best regards,<br><strong>${selectedBusiness.name}</strong></p>
              ${selectedBusiness.phone ? `<p style="color: #6b7280; font-size: 14px; margin: 5px 0 0 0;">Phone: ${selectedBusiness.phone}</p>` : ''}
              ${selectedBusiness.email ? `<p style="color: #6b7280; font-size: 14px; margin: 5px 0 0 0;">Email: ${selectedBusiness.email}</p>` : ''}
            </div>
          </div>
        </div>
      `;

      // Send email using Gmail integration with PDF attachment
      console.log('📧 Sending email with attachment...');
      const emailResult = await sendGmailEmail({
        businessId: selectedBusiness.id,
        to: selectedCustomer.email,
        subject: emailSubject,
        body: emailBody,
        attachments: [{
          filename: pdfData.filename,
          content: base64Content,
          type: 'application/pdf'
        }]
      });
      console.log('📧 Email result:', emailResult);

      if (emailResult.success) {
        // Dismiss loading toast and show success toast - same as booking payment summary
        portalToast.dismiss(loadingToast);
        portalToast.success('Invoice sent successfully! ✅');
        console.log('✅ Product sale invoice email sent successfully');
        
        // Clear cart and close modal
        clearCart();
        setSelectedCustomer(null);
        setAppliedDiscount(null);
        setIsSendInvoiceModalOpen(false);
        setLoadingToastId(null);
        
        // Refresh stock items
        loadItems();
      } else {
        throw new Error(emailResult.error || 'Failed to send email');
      }

    } catch (error) {
      console.error('❌ Failed to send invoice email:', error);
      console.error('Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        selectedCustomer: selectedCustomer?.email,
        selectedBusiness: selectedBusiness?.id,
        cartLength: cart.length
      });
      
      // Dismiss loading toast and show error toast - same as booking payment summary
      portalToast.dismiss(loadingToast);
      portalToast.error('Failed to send invoice. Please try again.');
    } finally {
      setIsSendingInvoice(false);
      setLoadingToastId(null);
    }
  };

  const handleCustomerSelected = (customer: any) => {
    setSelectedCustomer(customer);
    setIsCustomerModalOpen(false);
    // Don't automatically open payment modal - let user choose when to take payment
  };

  return (
    <div className="h-full bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg sm:text-2xl font-semibold text-gray-900">Sell Products</h1>
              <p className="text-xs sm:text-sm text-gray-600 hidden sm:block">Add products to cart and complete sales</p>
            </div>
            
            {/* Cart Summary - Icon only on mobile, full text on desktop */}
            <button
              onClick={() => setIsCartOpen(!isCartOpen)}
              className="relative bg-green-600 hover:bg-green-700 text-white p-2 sm:px-4 sm:py-2 rounded-lg flex items-center transition-colors"
            >
              <ShoppingCart className="h-5 w-5" />
              <span className="hidden sm:inline-block sm:ml-2">Cart ({cart.length})</span>
              {cart.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {cart.reduce((total, item) => total + item.quantity, 0)}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Main Products Area */}
        <div className="flex-1 flex flex-col">
          {/* Search Bar */}
          <div className="bg-white border-b border-gray-200 px-6 py-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
          </div>

          {/* Products Grid */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
              </div>
            ) : filteredItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                <Package2 className="h-12 w-12 mb-4" />
                <p className="text-lg font-medium">No products available</p>
                <p className="text-sm">No products found matching your search</p>
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-6 gap-2 sm:gap-3">
                {filteredItems.map((item) => (
                  <div key={item.id} className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                    {/* Product Image */}
                    <div className="h-28 sm:h-36 bg-gray-100 rounded-t-lg overflow-hidden">
                      {productPhotos[item.id] ? (
                        <img
                          src={productPhotos[item.id]}
                          alt={item.name}
                          className="w-full h-full object-contain hover:scale-105 transition-transform duration-200"
                          onError={(e) => {
                            // If image fails to load, hide it and show placeholder
                            e.currentTarget.style.display = 'none';
                            const placeholder = e.currentTarget.nextElementSibling as HTMLElement;
                            if (placeholder) placeholder.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div 
                        className="w-full h-full flex items-center justify-center"
                        style={{ display: productPhotos[item.id] ? 'none' : 'flex' }}
                      >
                        <Package2 className="h-12 w-12 text-gray-400" />
                      </div>
                    </div>
                    
                    {/* Product Info */}
                    <div className="p-3 sm:p-4">
                      <h3 className="font-medium text-gray-900 truncate text-sm sm:text-base">{item.name}</h3>
                      {item.description && (
                        <p className="text-xs sm:text-sm text-gray-600 mt-1 line-clamp-2">{item.description}</p>
                      )}
                      
                      <div className="mt-2 sm:mt-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm sm:text-lg font-semibold text-green-600 truncate">${item.price.toFixed(2)}</p>
                            <p className="text-xs text-gray-500">{item.quantity} in stock</p>
                          </div>
                        </div>
                        
                        {(() => {
                          const cartItem = cart.find(cartItem => cartItem.product.id === item.id);
                          
                          if (cartItem) {
                            // Show quantity controls if item is in cart - full width on mobile
                            return (
                              <div className="flex items-center bg-green-50 border border-green-200 rounded-lg w-full">
                                <button
                                  onClick={() => updateCartQuantity(item.id, cartItem.quantity - 1)}
                                  className="flex-1 p-1.5 sm:p-2 text-green-600 hover:bg-green-100 rounded-l-lg transition-colors flex items-center justify-center"
                                >
                                  <Minus className="h-3 w-3 sm:h-4 sm:w-4" />
                                </button>
                                <span className="px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-green-800 text-center min-w-[2rem] sm:min-w-[2.5rem]">
                                  {cartItem.quantity}
                                </span>
                                <button
                                  onClick={() => updateCartQuantity(item.id, cartItem.quantity + 1)}
                                  className="flex-1 p-1.5 sm:p-2 text-green-600 hover:bg-green-100 rounded-r-lg transition-colors flex items-center justify-center"
                                  disabled={cartItem.quantity >= item.quantity}
                                >
                                  <Plus className="h-3 w-3 sm:h-4 sm:w-4" />
                                </button>
                              </div>
                            );
                          } else {
                            // Show add to cart button if item is not in cart - full width on mobile
                            return (
                              <button
                                onClick={() => addToCart(item)}
                                className="bg-green-600 hover:bg-green-700 text-white p-1.5 sm:p-2 rounded-lg transition-colors w-full flex items-center justify-center"
                                disabled={item.quantity === 0}
                              >
                                <Plus className="h-3 w-3 sm:h-4 sm:w-4" />
                              </button>
                            );
                          }
                        })()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

         {/* Cart Modal */}
         {cart.length > 0 && isCartOpen && (
           <div className="fixed inset-0 bg-black bg-opacity-50 z-20 flex items-center justify-center p-4">
             <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] flex flex-col">
               {/* Cart Header */}
               <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                 <h3 className="text-lg font-semibold text-gray-900">Shopping Cart</h3>
                 <div className="flex items-center gap-2">
                   <button
                     onClick={() => setIsCartOpen(false)}
                     className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                   >
                     <X className="h-5 w-5 text-gray-500" />
                   </button>
                   <button
                     onClick={clearCart}
                     className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                     title="Clear Cart"
                   >
                     <Trash2 className="h-5 w-5 text-gray-500" />
                   </button>
                 </div>
               </div>

               {/* Cart Items */}
               <div className="flex-1 overflow-y-auto p-4">
                 <div className="space-y-3">
                   {cart.map((cartItem) => (
                     <div key={cartItem.product.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                       <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                         {productPhotos[cartItem.product.id] ? (
                           <img
                             src={productPhotos[cartItem.product.id]}
                             alt={cartItem.product.name}
                             className="w-full h-full object-cover rounded-lg"
                           />
                         ) : (
                           <Package2 className="h-6 w-6 text-gray-400" />
                         )}
                       </div>
                       <div className="flex-1 min-w-0">
                         <h4 className="font-medium text-gray-900 text-sm truncate">{cartItem.product.name}</h4>
                         <p className="text-sm text-gray-600">${cartItem.product.price?.toFixed(2) || '0.00'} each</p>
                       </div>
                       <div className="flex items-center gap-2">
                         <button
                           onClick={() => updateCartQuantity(cartItem.product.id, cartItem.quantity - 1)}
                           className="w-8 h-8 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center justify-center transition-colors"
                         >
                           <Minus className="h-4 w-4" />
                         </button>
                         <span className="font-medium text-gray-900 w-8 text-center">{cartItem.quantity}</span>
                         <button
                           onClick={() => updateCartQuantity(cartItem.product.id, cartItem.quantity + 1)}
                           className="w-8 h-8 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center justify-center transition-colors"
                         >
                           <Plus className="h-4 w-4" />
                         </button>
                       </div>
                     </div>
                   ))}
                 </div>
               </div>

               {/* Cart Footer */}
               <div className="border-t border-gray-200 p-4 space-y-4">
                 {/* Apply Discount Section */}
                 <div className="space-y-3">
                   {/* Show discount input only when no discount is applied */}
                   {!appliedDiscount && (
                     <>
                       <div className="flex items-center justify-between">
                         <span className="text-sm font-medium text-gray-700">Apply Discount</span>
                         <div className="flex bg-gray-100 rounded-lg p-1">
                           <button
                             onClick={() => {
                               setDiscountMode('code');
                               setDiscountError('');
                             }}
                             className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                               discountMode === 'code'
                                 ? 'bg-green-600 text-white'
                                 : 'text-gray-600 hover:text-gray-900'
                             }`}
                           >
                             Code
                           </button>
                           <button
                             onClick={() => {
                               setDiscountMode('custom');
                               setDiscountError('');
                             }}
                             className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                               discountMode === 'custom'
                                 ? 'bg-green-600 text-white'
                                 : 'text-gray-600 hover:text-gray-900'
                             }`}
                           >
                             Custom
                           </button>
                         </div>
                       </div>

                       {/* Discount Code Input */}
                       {discountMode === 'code' && (
                         <div className="flex gap-2">
                           <input
                             type="text"
                             placeholder="Enter discount code"
                             value={discountCode}
                             onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                             onKeyPress={(e) => e.key === 'Enter' && !discountValidating && applyDiscountCode()}
                             disabled={discountValidating}
                             className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:bg-gray-100 uppercase"
                           />
                           <button
                             onClick={applyDiscountCode}
                             disabled={discountValidating}
                             className="px-3 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white text-sm rounded-lg transition-colors"
                           >
                             {discountValidating ? 'Validating...' : 'Apply'}
                           </button>
                         </div>
                       )}

                       {/* Custom Discount Input */}
                       {discountMode === 'custom' && (
                         <div className="flex gap-2">
                           <input
                             type="number"
                             placeholder="Amount"
                             value={customDiscountAmount}
                             onChange={(e) => setCustomDiscountAmount(e.target.value)}
                             className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                           />
                           <select
                             value={customDiscountType}
                             onChange={(e) => setCustomDiscountType(e.target.value as '%' | '$')}
                             className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                           >
                             <option value="%">%</option>
                             <option value="$">$</option>
                           </select>
                           <button
                             onClick={applyCustomDiscount}
                             className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition-colors"
                           >
                             Apply
                           </button>
                         </div>
                       )}

                       {/* Discount Error */}
                       {discountError && (
                         <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                           <div className="flex items-center gap-2">
                             <AlertCircle className="h-4 w-4 text-red-600" />
                             <span className="text-sm text-red-700">{discountError}</span>
                           </div>
                         </div>
                       )}
                     </>
                   )}

                   {/* Applied Discount Display - Only show when discount is applied */}
                   {appliedDiscount && (
                     <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
                       <div className="flex items-center gap-2">
                         <Check className="h-4 w-4 text-green-600" />
                         <span className="text-sm font-medium text-green-900">
                           {appliedDiscount.source === 'code' 
                             ? `Code: ${appliedDiscount.code} (${appliedDiscount.type === 'percentage' ? `${appliedDiscount.value}%` : `$${appliedDiscount.value.toFixed(2)}`})`
                             : appliedDiscount.description}
                         </span>
                       </div>
                       <button
                         onClick={removeDiscount}
                         className="text-green-600 hover:text-green-800 transition-colors"
                       >
                         <X className="h-4 w-4" />
                       </button>
                     </div>
                   )}
                 </div>

                 {/* Add Customer Section */}
                 <div className="space-y-3">
                   {selectedCustomer ? (
                     <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-3">
                       <div className="flex items-center gap-3">
                         <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
                           {selectedCustomer.first_name?.[0]}{selectedCustomer.last_name?.[0]}
                         </div>
                         <div>
                           <p className="text-sm font-medium text-blue-900">
                             {selectedCustomer.first_name} {selectedCustomer.last_name}
                           </p>
                           {selectedCustomer.email && (
                             <div className="flex items-center gap-1">
                               <Mail className="h-3 w-3 text-blue-600" />
                               <p className="text-xs text-blue-700">{selectedCustomer.email}</p>
                             </div>
                           )}
                           {selectedCustomer.phone && (
                             <div className="flex items-center gap-1">
                               <Phone className="h-3 w-3 text-blue-600" />
                               <p className="text-xs text-blue-700">{selectedCustomer.phone}</p>
                             </div>
                           )}
                         </div>
                       </div>
                       <button
                         onClick={() => setSelectedCustomer(null)}
                         className="text-blue-600 hover:text-blue-800 transition-colors"
                       >
                         <X className="h-4 w-4" />
                       </button>
                     </div>
                   ) : (
                     <button
                       onClick={() => setIsCustomerModalOpen(true)}
                       className="w-full flex items-center justify-center gap-2 py-3 px-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors"
                     >
                       <Plus className="h-4 w-4" />
                       Add Customer
                     </button>
                   )}
                 </div>

                 {/* Totals */}
                 <div className="space-y-2 pt-3 border-t border-gray-200">
                   <div className="flex justify-between text-sm">
                     <span className="text-gray-600">Subtotal:</span>
                     <span className="font-medium">${cartSubtotal.toFixed(2)}</span>
                   </div>
                   {appliedDiscount && (
                     <div className="flex justify-between text-sm">
                       <span className="text-green-600">Discount:</span>
                       <span className="font-medium text-green-600">-${discountAmount.toFixed(2)}</span>
                     </div>
                   )}
                   <div className="flex justify-between text-sm">
                     <span className="text-gray-600">Tax (5%):</span>
                     <span className="font-medium">${cartTaxAmount.toFixed(2)}</span>
                   </div>
                   <div className="flex justify-between text-lg font-bold border-t border-gray-200 pt-2">
                     <span>Total:</span>
                     <span>${cartTotal.toFixed(2)}</span>
                   </div>
                 </div>

                 <div className="flex gap-3">
                   <button
                     onClick={handleSendInvoice}
                     className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                   >
                     <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 7.89a2 2 0 002.83 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                     </svg>
                     Send Invoice
                   </button>
                   <button
                     onClick={() => setIsPaymentModalOpen(true)}
                     className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                   >
                     <CreditCard className="h-4 w-4" />
                     Take Payment
                   </button>
                 </div>
               </div>
             </div>
           </div>
         )}
      </div>

      {/* Modals */}
      {isCustomerModalOpen && (
        <SelectCustomerModal
          isOpen={isCustomerModalOpen}
          onClose={() => setIsCustomerModalOpen(false)}
          onSelect={handleCustomerSelected}
        />
      )}

       {isPaymentModalOpen && (
         <RecordProductSalePaymentModal
           isOpen={isPaymentModalOpen}
           onClose={() => setIsPaymentModalOpen(false)}
           onSuccess={() => {
             setIsPaymentModalOpen(false);
             clearCart();
             setSelectedCustomer(null);
             setAppliedDiscount(null);
             // Refresh stock items to update quantities
             loadItems();
           }}
           businessId={selectedBusiness?.id || ''}
           cart={cart}
           selectedCustomer={selectedCustomer}
           appliedDiscount={appliedDiscount}
           subtotal={cartSubtotal}
           discountAmount={discountAmount}
           taxAmount={cartTaxAmount}
           total={cartTotal}
         />
       )}

      {isSendInvoiceModalOpen && selectedCustomer && (
        <SendInvoiceModal
          isOpen={isSendInvoiceModalOpen}
          onClose={() => setIsSendInvoiceModalOpen(false)}
          customerEmail={selectedCustomer.email}
          customerPhone={selectedCustomer.phone}
          hasStripeConnected={!!(selectedBusiness as any)?.stripe_connect_account_id}
          onSelectMethod={handleSendInvoiceMethod}
        />
      )}

      {/* Portal Toast for Modal Context */}
      {showPortalToast && (
        <PortalToast
          type={portalToastType}
          onClose={() => setShowPortalToast(false)}
        >
          {portalToastMessage}
        </PortalToast>
      )}
    </div>
  );
}

export default SellProducts;
